import React, { useEffect, useState } from 'react'

import { useHistory, useParams } from 'react-router-dom'
import { useLastLocation } from 'react-router-last-location'

import { get } from 'lodash'
import moment from 'moment'

import ArrowLeftIcon from '@ant-design/icons/ArrowLeftOutlined'
import { Col, message, Modal, PageHeader, Popconfirm, Row, Typography } from 'antd'

import { observer } from 'mobx-react-lite'

import { trackEvent } from 'common/analytics/event_tracking'
import { Events } from 'common/analytics/events'
import { Box } from 'common/components/boxes'
import { Loading } from 'common/components/Loading'
import { OrderBlockedHeader } from 'common/components/OrderBlockedHeader'
import { OrderBlockedModals } from 'common/components/OrderBlockedModals'
import { Page } from 'common/components/Page'
import UnloadWarning from 'common/components/unload_warning'
import { Visibility } from 'common/components/Visibility'
import { getOrderTitleType } from 'common/helpers/get_order_type_title'
import { useQuery } from 'common/hooks/use-query'
import { OrderStates, OrderSubStates } from 'common/server/server_types'

import { Attachments } from 'vendor/components/Attachments/attachments'
import { OrderActions } from 'vendor/components/OrderActions/order_actions'
import { OrderInfo } from 'vendor/components/OrderInfo/order_info'
import { VendorOrderState } from 'vendor/components/OrderState'
import { QuoteExpiration } from 'vendor/components/QuoteExpiration'
import { RequestedCancellation } from 'vendor/components/RequestedCancellation'
import { useLockPo } from 'vendor/hooks/use-lock-po'
import { useVendorStores } from 'vendor/hooks/use-stores'
import { OrderUpdateParams } from 'vendor/server/orders'

import { Materials } from './materials'
import { MessagesAndHistory } from './messages_and_history'

const oldOrderVersionErrorMessage =
  'You are trying to save an order that has been recently updated by the customer. Please refresh the page and try again; unfortunately your changes will not be saved.'

const OrderDetail = observer(() => {
  const { orderStore, uploaderStore, vendorSettingStore, unitsStore, userStore } = useVendorStores()
  const params = useParams()
  const lastLocation = useLastLocation()
  const history = useHistory()

  const { isLoading } = useQuery(() => orderStore.selectOrder(params['id']))
  useQuery(vendorSettingStore.indexUsers)
  useQuery(unitsStore.maybeUnits)

  const { orderSession } = useLockPo(orderStore?.selectedOrder)
  const isRFQ = orderStore?.selectedOrder?.state === OrderStates.QUOTED

  const [watcherIds, setWatcherIds] = useState([])
  const [isSubmitting, setSubmitting] = useState(false)

  const disabled = orderSession.isBlocked

  useEffect(() => {
    if (orderStore.selectedOrder) {
      setWatcherIds(orderStore.selectedOrder?.vendor_contacts?.map((contact) => contact?.vendor_user?.id))
    }
  }, [orderStore.selectedOrder?.id])

  const goBack = (isRefresh = false) => {
    orderStore.selectOrder(null)
    if (lastLocation) {
      const search = new URLSearchParams(lastLocation?.search)

      history.push({
        ...lastLocation,
        search: `?${search.toString()}`,
      })
    } else {
      history.push('/vendor/orders', {
        search: isRefresh && '?refresh=true',
      })
    }
  }

  const handleSave = (stateChanges: OrderUpdateParams['state_changes']) => {
    return orderStore.updateOrder(
      stateChanges,
      uploaderStore.signedIds('vendor_files'),
      uploaderStore.deleteAttachmentIds['vendor_files'],
      get(uploaderStore.signedIds('quote'), '[0]'),
      watcherIds,
    )
  }

  const handleUpdate = async (stateChanges: OrderUpdateParams['state_changes']) => {
    if (!watcherIds.length) {
      Modal.error({ title: "Watchers can't be blank." })
      return
    }

    if (!uploaderStore.checkIfAllUploadsCompleted()) {
      Modal.error({ title: 'Uploads have not completed yet, please try again.' })
      return
    }

    const { state, sub_state } = orderStore.selectedOrder

    try {
      setSubmitting(true)
      await handleSave(stateChanges)
      goBack(true)

      let notificationMessage: string
      if (stateChanges.rejected_at) {
        notificationMessage = `Rejected ${state == OrderStates.QUOTED ? 'RFQ' : 'Order'}`
      } else if (state == OrderStates.CANCELLED) {
        notificationMessage = sub_state == OrderSubStates.CANCELLED_QUOTE_BY_VENDOR ? 'Re-opened order' : 'Updated RFQ'
      } else if (state == OrderStates.ORDERED && sub_state == OrderSubStates.ORDERED_REQUESTED) {
        notificationMessage = 'Confirmed Order'
      } else {
        notificationMessage = `Updated ${state == OrderStates.QUOTED ? 'RFQ' : 'Order'}`
      }

      message.open({
        content: notificationMessage,
        duration: 8,
      })
    } catch (err) {
      const backendMessage = err?.response?.data?.error || err?.message
      if (backendMessage) {
        message.error({
          content: backendMessage,
          duration: 8,
        })
        if (backendMessage === oldOrderVersionErrorMessage) {
          trackEvent(Events.VENDOR_SAVE_OLD_ORDER_VERSION, location.pathname.slice(1), {
            order_id: orderStore.selectedOrder?.id,
          })
        }
      } else {
        message.error('Unable to save updates to the order')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleConfirmCancel = async () => {
    try {
      setSubmitting(true)
      await handleSave({ cancellation_accepted_at: moment().toISOString(), cancellation_rejected_at: null })
      goBack(true)

      message.open({
        content: 'Order Cancelled',
      })
    } catch (err) {
      const backendMessage = err?.response?.data?.error || err?.message
      message.error(backendMessage || 'Unable to cancel the order')
    } finally {
      setSubmitting(false)
    }
  }

  const handleRejectCancel = async () => {
    try {
      setSubmitting(true)
      await handleSave({ cancellation_accepted_at: null, cancellation_rejected_at: moment().toISOString() })
      goBack(true)

      message.open({
        content: 'Rejected Order Cancelling',
      })
    } catch (err) {
      const backendMessage = err?.response?.data?.error || err?.message
      message.error(backendMessage || 'Unable to reject canceling the order')
    } finally {
      setSubmitting(false)
    }
  }

  if (isLoading) {
    return <Loading />
  }

  if (!orderStore.selectedOrder) {
    return null
  }

  const { state, sub_state, order_package_name, deliveries = [] } = orderStore.selectedOrder

  const orderTypeTitle = getOrderTitleType(state)

  const quoteExpirationProps = {
    quoteScheduledToExpireAt: orderStore.selectedOrder.quote_scheduled_to_expire_at,
    defaultQuoteValidForDays: userStore.currentUser?.vendor?.default_quote_valid_for_days,
    onChange: (date) => {
      orderStore.selectedOrder.quote_scheduled_to_expire_at = date
      orderStore.setOrderDirty(true)
    },
    onSetDefaultExpiration: (date) => {
      orderStore.selectedOrder.quote_scheduled_to_expire_at = date
    },
  }

  return (
    <>
      <Page>
        <Page.Header>
          <PageHeader
            title={
              <Box
                display="flex"
                alignItems={{ _: 'flex-start', md: 'center' }}
                flexDirection={{ _: 'column', md: 'row' }}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  <Typography.Text style={{ marginRight: 20 }}>{orderTypeTitle}</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 16 }}>
                    {order_package_name}
                  </Typography.Text>
                </Box>

                <Box ml={{ _: 0, md: 20 }} display="flex" alignItems="center">
                  <VendorOrderState
                    data-cy="vendor-order-state"
                    state={state}
                    sub_state={sub_state}
                    showIssue={deliveries?.some((d) => d.has_open_issue)}
                    deliveryIssues={deliveries?.map((d) => d.delivery_issue_type)}
                    size="medium"
                  />

                  {orderStore.selectedOrder.state === OrderStates.QUOTED && (
                    <Visibility.Show breakpoint="md">
                      <QuoteExpiration {...quoteExpirationProps} />
                    </Visibility.Show>
                  )}
                </Box>
              </Box>
            }
            style={{ padding: '0', width: '100%' }}
            extra={[
              ...(orderStore.selectedOrder.state === OrderStates.QUOTED
                ? [
                    <Visibility.Hidden breakpoint="md" key="quote-expire">
                      <QuoteExpiration {...quoteExpirationProps} />
                    </Visibility.Hidden>,
                  ]
                : []),
              <Visibility.Hidden breakpoint="md" key="actions">
                <OrderActions
                  order={orderStore.selectedOrder}
                  orderDirty={orderStore.selectedOrderDirty}
                  updateOrder={handleUpdate}
                  isSubmitting={isSubmitting}
                  disabled={disabled}
                />
              </Visibility.Hidden>,
            ]}
            onBack={() => null}
            backIcon={
              orderStore.selectedOrderDirty ? (
                <Popconfirm
                  data-cy={`dirty-order-back`}
                  title="You may have unsaved changes, are you sure you want to exit?"
                  onConfirm={() => goBack(false)}
                  okText="Yes"
                  cancelText="No"
                >
                  <ArrowLeftIcon />
                </Popconfirm>
              ) : (
                <ArrowLeftIcon data-cy={`order-back-arrow`} onClick={() => goBack(false)} />
              )
            }
          />

          <OrderBlockedHeader {...orderSession} isRFQ={isRFQ} />
        </Page.Header>

        <Page.Content p={0} px={{ _: 0, sm: 16 }} pt={16} pb={{ _: 100, md: 32 }} overflowX="hidden">
          <RequestedCancellation
            cancellationRequestedAt={orderStore.selectedOrder?.cancellation_requested_at}
            cancellationAcceptedAt={orderStore.selectedOrder?.cancellation_accepted_at}
            cancellationRejectedAt={orderStore.selectedOrder?.cancellation_rejected_at}
            cancellationRequestedReason={orderStore.selectedOrder?.cancellation_requested_reason}
            onConfirmCancel={handleConfirmCancel}
            onRejectCancel={handleRejectCancel}
            isSubmitting={isSubmitting}
            disabled={disabled}
          />

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <OrderInfo
                order={orderStore.selectedOrder}
                watcherIds={watcherIds}
                onChangeWatchers={(value) => {
                  setWatcherIds(value)
                  orderStore.setOrderDirty()
                }}
                disabled={disabled}
              />
            </Col>

            <Col span={24}>
              <Materials isPoLocked={orderSession.isBlocked} />
            </Col>

            <Col xs={24} lg={12} xxl={10}>
              <Attachments
                data-cy="vendor-attachments"
                order={orderStore.selectedOrder}
                onChange={(isDirty) => (orderStore.selectedOrderDirty = isDirty)}
                addExistingFiles={uploaderStore.addExistingFiles}
                resetAllUploads={uploaderStore.resetAllUploads}
                onRemoveUpload={uploaderStore.removeUpload}
                onAddNewUpload={uploaderStore.addNewUpload}
                onResetUploads={uploaderStore.resetUploads}
                onSetUploadError={uploaderStore.setUploadError}
                onUpdateUpload={uploaderStore.updateUpload}
                fileList={uploaderStore.fileList}
                accept=".pdf,application/pdf"
                disabled={disabled}
              />
            </Col>

            <Col xs={24} lg={12} xxl={14}>
              <MessagesAndHistory data-cy="vendor-messages-and-history" />
            </Col>
          </Row>
        </Page.Content>

        <Visibility.Show breakpoint="md">
          <OrderActions
            order={orderStore.selectedOrder}
            orderDirty={orderStore.selectedOrderDirty}
            updateOrder={handleUpdate}
            isSubmitting={isSubmitting}
            disabled={disabled}
            isFloat
          />
        </Visibility.Show>

        <UnloadWarning showWarning={() => orderStore.selectedOrderDirty} />
      </Page>

      <OrderBlockedModals
        {...orderSession}
        onCancelModalOrderExpired={goBack}
        onCancelModalOrderReleased={goBack}
        isRFQ={isRFQ}
      />
    </>
  )
})

export default OrderDetail
